import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { 
  BarChart3, Users, CheckCircle, XCircle, Clock, 
  Download, ArrowLeft, TrendingUp, Award, AlertTriangle 
} from 'lucide-react';

const QuizReports = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadData();
  }, [quizId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load quiz details
      const quizRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/quizzes/${quizId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (quizRes.ok) {
        const quizData = await quizRes.json();
        setQuiz(quizData);
      }

      // Load attempts
      const attemptsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/quizzes/${quizId}/attempts`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (attemptsRes.ok) {
        const attemptsData = await attemptsRes.json();
        setAttempts(attemptsData);
        calculateStats(attemptsData);
      }

      // Load questions
      const questionsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/quizzes/${quizId}/questions`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (questionsRes.ok) {
        const questionsData = await questionsRes.json();
        setQuestions(questionsData);
      }
    } catch (error) {
      console.error('Veri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (attemptsData) => {
    const completed = attemptsData.filter(a => a.completedAt);
    if (completed.length === 0) {
      setStats({
        totalAttempts: 0,
        completedAttempts: 0,
        averageScore: 0,
        passRate: 0,
        highestScore: 0,
        lowestScore: 0
      });
      return;
    }

    const scores = completed.map(a => a.percentage || 0);
    const passCount = completed.filter(a => a.passed).length;

    setStats({
      totalAttempts: attemptsData.length,
      completedAttempts: completed.length,
      averageScore: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
      passRate: Math.round((passCount / completed.length) * 100),
      highestScore: Math.max(...scores),
      lowestScore: Math.min(...scores)
    });
  };

  const exportToExcel = () => {
    // Simple CSV export
    const headers = ['Öğrenci', 'Email', 'Puan', 'Yüzde', 'Durum', 'Tarih'];
    const rows = attempts
      .filter(a => a.completedAt)
      .map(a => [
        a.user ? `${a.user.firstName || ''} ${a.user.lastName || ''}`.trim() : '-',
        a.user?.email || '-',
        a.score || 0,
        `%${a.percentage || 0}`,
        a.passed ? 'Geçti' : 'Kaldı',
        new Date(a.completedAt).toLocaleDateString('tr-TR')
      ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${quiz?.title || 'quiz'}_sonuclar.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} />
            Geri
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">📊 {quiz?.title} - Sonuç Raporu</h1>
              <p className="text-gray-500">{quiz?.questionCount} soru • {quiz?.totalPoints} puan</p>
            </div>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download size={18} />
              Excel'e Aktar
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <StatCard
            icon={<Users className="text-blue-600" />}
            label="Toplam Deneme"
            value={stats?.totalAttempts || 0}
          />
          <StatCard
            icon={<CheckCircle className="text-green-600" />}
            label="Tamamlanan"
            value={stats?.completedAttempts || 0}
          />
          <StatCard
            icon={<TrendingUp className="text-purple-600" />}
            label="Ortalama"
            value={`%${stats?.averageScore || 0}`}
          />
          <StatCard
            icon={<Award className="text-yellow-600" />}
            label="Geçme Oranı"
            value={`%${stats?.passRate || 0}`}
          />
          <StatCard
            icon={<BarChart3 className="text-green-600" />}
            label="En Yüksek"
            value={`%${stats?.highestScore || 0}`}
          />
          <StatCard
            icon={<BarChart3 className="text-red-600" />}
            label="En Düşük"
            value={`%${stats?.lowestScore || 0}`}
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Attempts List */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-lg">Öğrenci Sonuçları</h2>
            </div>
            
            {attempts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Henüz quiz çözülmemiş
              </div>
            ) : (
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {attempts.filter(a => a.completedAt).map(attempt => (
                  <div key={attempt.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          attempt.passed ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {attempt.passed ? (
                            <CheckCircle className="text-green-600" size={20} />
                          ) : (
                            <XCircle className="text-red-600" size={20} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {attempt.user 
                              ? `${attempt.user.firstName || ''} ${attempt.user.lastName || ''}`.trim() || attempt.user.email
                              : 'Bilinmeyen Kullanıcı'
                            }
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(attempt.completedAt).toLocaleDateString('tr-TR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${
                          attempt.passed ? 'text-green-600' : 'text-red-600'
                        }`}>
                          %{attempt.percentage}
                        </p>
                        <p className="text-sm text-gray-500">
                          {attempt.score}/{attempt.totalPoints} puan
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Question Analysis */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-lg">Soru Analizi</h2>
            </div>
            
            <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
              {questions.map((question, index) => {
                // Calculate correct answer rate for this question
                const completedAttempts = attempts.filter(a => a.completedAt && a.answers);
                const correctCount = completedAttempts.filter(a => {
                  const answer = a.answers?.find(ans => ans.questionId === question.id);
                  if (!answer) return false;
                  const correctOption = question.options?.find(o => o.isCorrect);
                  return answer.selectedOptionId === correctOption?.id;
                }).length;
                const correctRate = completedAttempts.length > 0 
                  ? Math.round((correctCount / completedAttempts.length) * 100) 
                  : 0;

                return (
                  <div key={question.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Soru {index + 1}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {question.questionText}
                        </p>
                      </div>
                      <div className={`text-right ${
                        correctRate >= 70 ? 'text-green-600' : 
                        correctRate >= 40 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        <p className="text-lg font-bold">%{correctRate}</p>
                        <p className="text-xs">doğru</p>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          correctRate >= 70 ? 'bg-green-500' : 
                          correctRate >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${correctRate}%` }}
                      />
                    </div>
                  </div>
                );
              })}

              {questions.length === 0 && (
                <p className="text-center text-gray-500 py-4">Soru bulunamadı</p>
              )}
            </div>
          </div>
        </div>

        {/* Performance Distribution */}
        <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
          <h2 className="font-semibold text-lg mb-4">Performans Dağılımı</h2>
          <div className="grid grid-cols-5 gap-2">
            {[
              { range: '0-20', color: 'bg-red-500' },
              { range: '21-40', color: 'bg-orange-500' },
              { range: '41-60', color: 'bg-yellow-500' },
              { range: '61-80', color: 'bg-lime-500' },
              { range: '81-100', color: 'bg-green-500' }
            ].map(({ range, color }) => {
              const [min, max] = range.split('-').map(Number);
              const count = attempts.filter(a => 
                a.completedAt && 
                a.percentage >= min && 
                a.percentage <= max
              ).length;
              const percentage = stats?.completedAttempts 
                ? Math.round((count / stats.completedAttempts) * 100) 
                : 0;

              return (
                <div key={range} className="text-center">
                  <div className="h-24 bg-gray-100 rounded-lg relative overflow-hidden flex items-end">
                    <div 
                      className={`w-full ${color} transition-all`}
                      style={{ height: `${percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">%{range}</p>
                  <p className="text-sm font-medium">{count} kişi</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value }) => (
  <div className="bg-white rounded-xl shadow-sm p-4">
    <div className="flex items-center gap-3">
      <div className="p-2 bg-gray-100 rounded-lg">
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  </div>
);

export default QuizReports;
